import { useState, useRef, useCallback, useEffect } from "react";
import {
  MdSync,
  MdShoppingBag,
  MdArrowBack,
  MdArrowForward,
  MdInfo,
} from "react-icons/md";
import "./ProductSelector.css";
import ProductCard from "../../components/ProductCard/ProductCard";
import SearchBar from "../../components/SearchBar/SearchBar";
import FilterOptions from "../../components/FilterOptions/FilterOptions";
import ProductUploadModal from "../../components/ProductUploadModal/ProductUploadModal";
import ZoomModal from "../../components/ZoomModal/ZoomModal";
import MobileZoomModal from "../../components/MobileZoomModal/MobileZoomModal";
import InternalPagination from "../../components/InternalPagination/InternalPagination";
import { useProductSelector } from "../../customHook/ProductSelector/useProductSelector";

const CollectionRail = ({
  title,
  products = [],
  selectedProduct,
  userRole,
  getGenerationCount,
  handleProductCardClick,
  handleToggleProduct,
  handleEditModalOpen,
  openZoomModal,
}) => {
  const railRef = useRef(null);
  const [scrollState, setScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
  });

  const updateScrollState = useCallback(() => {
    if (railRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = railRef.current;
      setScrollState({
        canScrollLeft: scrollLeft > 2,
        canScrollRight: scrollLeft + clientWidth < scrollWidth - 2,
      });
    }
  }, []);

  useEffect(() => {
    const el = railRef.current;
    if (!el) return;

    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);

    const timer = setTimeout(updateScrollState, 150);

    return () => {
      clearTimeout(timer);
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [products, updateScrollState]);

  const scrollRail = (direction) => {
    if (railRef.current) {
      const scrollAmount = railRef.current.clientWidth * 0.8;
      railRef.current.scrollBy({
        left: direction * scrollAmount,
        behavior: "smooth",
      });
    }
  };

  if (!products || products.length === 0) return null;

  return (
    <div className="mb-5">
      <div className="flex justify-between items-center mb-4 border-b border-outline/30 pb-2.5">
        <div className="flex items-center gap-2.5 border-l-[3px] border-primary pl-2.5">
          <h2 className="font-headline-md text-[18px] text-primary font-bold tracking-tight">
            {title}
          </h2>
          <span className="font-body-md inline-flex items-center gap-1.5 text-primary bg-primary/[0.04] border border-primary/10 px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-bold shadow-[0_1px_2px_rgba(0,0,0,0.02)] whitespace-nowrap">
            <span className="w-1.5 h-1.5 rounded-full bg-primary/40" />
            {products.length} product{products.length > 1 ? "s" : ""}
          </span>
        </div>

        {products.length > 1 && (
          <div className="flex gap-1.5">
            <button
              onClick={() => scrollRail(-1)}
              disabled={!scrollState.canScrollLeft}
              className={`w-7 h-7 rounded-full border flex items-center justify-center transition-all shadow-sm focus:outline-none ${
                scrollState.canScrollLeft
                  ? "text-slate-700 border-slate-300 hover:text-slate-900 hover:border-slate-500 active:scale-95 cursor-pointer bg-white"
                  : "text-slate-300 bg-slate-50 border-slate-200/80 cursor-not-allowed"
              }`}
              type="button"
              aria-label="Scroll left"
            >
              <MdArrowBack size={16} />
            </button>
            <button
              onClick={() => scrollRail(1)}
              disabled={!scrollState.canScrollRight}
              className={`w-7 h-7 rounded-full border flex items-center justify-center transition-all shadow-sm focus:outline-none ${
                scrollState.canScrollRight
                  ? "text-slate-700 border-slate-300 hover:text-slate-900 hover:border-slate-500 active:scale-95 cursor-pointer bg-white"
                  : "text-slate-300 bg-slate-50 border-slate-200/80 cursor-not-allowed"
              }`}
              type="button"
              aria-label="Scroll right"
            >
              <MdArrowForward size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Horizontal Scroll Rail */}
      <div
        ref={railRef}
        className="flex gap-4 overflow-x-auto rail-container pt-0.5 pb-2"
      >
        {products.map((item, i) => (
          <ProductCard
            key={item.id || item.iProductId || `${title}-${i}`}
            selectedProduct={selectedProduct}
            title={item.title}
            sku={
              item.sku ||
              item.vSku ||
              item.vSKU ||
              item.skuId ||
              item.iProductId ||
              item.id
            }
            price={
              item.price ??
              item.decPrice ??
              item.decPriceUsd ??
              item.decPriceInr
            }
            images={item.images}
            generationCount={getGenerationCount(item)}
            onSelect={() => handleProductCardClick(item)}
            bIsActive={item.bIsActive ?? item.isActive ?? true}
            canToggle={userRole === "admin"}
            onToggle={() => handleToggleProduct(item)}
            canEdit={userRole === "admin"}
            onEdit={() => handleEditModalOpen(item)}
            onZoom={openZoomModal}
          />
        ))}
      </div>
    </div>
  );
};

const ProductSelector = ({
  setSelectedProduct,
  setStep,
  selectedProduct,
  modelsData,
  backgroundsData,
}) => {
  const {
    userRole,
    category,
    onCategoryChange,
    searchTerm,
    onSearchChange,
    collections,
    filteredTop,
    filteredTrendy,
    onFilterChange,
    apiFilters,
    topSectionTitle,
    trendySectionTitle,
    modelType,
    mappedModelType,
    onModelTypeChange,
    refreshProducts,
    loading,
    pagination,
    onPageChange,
    getGenerationCount,

    // Refs & Scroll
    topRailRef,
    bottomRailRef,
    topScroll,
    bottomScroll,
    scrollRail,

    // Selection & Navigation
    handleProductCardClick,
    handleContinue,

    // Modals
    isUploadModalOpen,
    editingProduct,
    isProductUploadLoading,
    handleEditModalOpen,
    handleUploadModalClose,
    handleUploadSubmit,
    fetchFilterCategories,
    fetchCollectionCategories,

    // Zoom modal
    zoomModal,
    isMobileZoomOpen,
    openZoomModal,
    closeZoomModal,

    // Admin handlers
    handleToggleProduct,
  } = useProductSelector({
    setSelectedProduct,
    setStep,
    selectedProduct,
    modelsData,
    backgroundsData,
  });

  const displayCollections =
    Array.isArray(collections) && collections.length > 0
      ? collections
      : [
          { title: topSectionTitle, products: filteredTop },
          { title: trendySectionTitle, products: filteredTrendy },
        ].filter((col) => Array.isArray(col.products) && col.products.length > 0);

  const hasAnyProducts = displayCollections.some(
    (col) => Array.isArray(col.products) && col.products.length > 0
  );

  return (
    <>
      <div className="app-page product-selector-screen flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Scrollable content body */}
        <div className="flex-1 overflow-y-auto min-h-0 pb-16">
          {/* Workspace Stepper */}
          <div className="product-selector-header px-4 sm:px-6 pt-3 sm:pt-4 pb-2 sm:pb-3 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2.5 lg:gap-4">
            <div>
              <h2 className="font-headline-md text-lg sm:text-[20px] text-primary mb-0.5 font-bold tracking-tight">
                Select Product
              </h2>
              <p className="font-body-md text-[10px] text-secondary">
                Choose the garment you want to generate.
              </p>
            </div>

            <div className="app-glass-panel product-stepper flex items-center px-3 sm:px-4 py-1.5 sm:py-2 self-stretch lg:self-auto">
              <div className="product-stepper-track flex items-center gap-2 sm:gap-4 text-xs font-medium">
                <div className="flex items-center gap-1.5 sm:gap-2 text-primary">
                  <span className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-primary text-on-primary flex items-center justify-center text-[9px] sm:text-[10px] font-bold ring-2 sm:ring-4 ring-primary/10">
                    1
                  </span>
                  <span className="font-semibold">Product</span>
                </div>
                <div className="w-4 sm:w-6 h-0.5 bg-outline-variant"></div>
                <div className="flex items-center gap-1.5 sm:gap-2 text-secondary">
                  <span className="w-4 h-4 sm:w-5 sm:h-5 rounded-full border border-outline-variant bg-surface-container flex items-center justify-center text-[9px] sm:text-[10px] font-semibold">
                    2
                  </span>
                  <span>Model</span>
                </div>
                <div className="w-4 sm:w-6 h-0.5 bg-outline-variant"></div>
                <div className="flex items-center gap-1.5 sm:gap-2 text-secondary">
                  <span className="w-4 h-4 sm:w-5 sm:h-5 rounded-full border border-outline-variant bg-surface-container flex items-center justify-center text-[9px] sm:text-[10px] font-semibold">
                    3
                  </span>
                  <span>Background</span>
                </div>
                <div className="w-4 sm:w-6 h-0.5 bg-outline-variant"></div>
                <div className="flex items-center gap-1.5 sm:gap-2 text-secondary">
                  <span className="w-4 h-4 sm:w-5 sm:h-5 rounded-full border border-outline-variant bg-surface-container flex items-center justify-center text-[9px] sm:text-[10px] font-semibold">
                    4
                  </span>
                  <span>Review</span>
                </div>
              </div>
            </div>
          </div>

          {/* Primary Controls (Sticky at top when scrolling) */}
          <div className="product-controls-wrapper sticky top-0 z-30 px-4 sm:px-6 py-2 bg-[#fff] ">
            <div className="app-control-bar product-controls-shell flex flex-col lg:flex-row lg:items-center justify-between gap-2.5 sm:gap-4 p-2.5 sm:p-3">
              <div className="product-primary-controls flex items-center justify-between gap-2 sm:gap-4 w-full lg:w-auto">
                {/* Generation Type selector tabs */}
                <div className="generation-type-control flex items-center gap-2 sm:gap-3">
                  <span className="hidden lg:inline-block text-[10px] text-secondary uppercase tracking-wider font-bold">
                    Generation Type : 
                  </span>
                  <div className="generation-type-tabs inline-flex bg-slate-100/80 p-0.5 rounded-md gap-0.5">
                    <button
                      onClick={() => onModelTypeChange("mannequin")}
                      className={`px-2.5 sm:px-3.5 py-1 rounded font-semibold text-xs transition-all duration-200 focus:outline-none ${
                        modelType === "mannequin"
                          ? "bg-primary text-on-primary shadow-sm font-bold border-none"
                          : "bg-white text-slate-500 border border-slate-200 hover:text-primary hover:border-slate-300 hover:shadow-sm"
                      }`}
                      type="button"
                    >
                      Mannequin
                    </button>
                    <button
                      onClick={() => onModelTypeChange("model")}
                      className={`px-2.5 sm:px-3.5 py-1 rounded font-semibold text-xs transition-all duration-200 focus:outline-none ${
                        modelType === "model"
                          ? "bg-primary text-on-primary shadow-sm font-bold border-none"
                          : "bg-white text-slate-500 border border-slate-200 hover:text-primary hover:border-slate-300 hover:shadow-sm"
                      }`}
                      type="button"
                    >
                      Model
                    </button>
                  </div>
                </div>

                {/* Gender selector tabs */}
                <div className="gender-tabs flex gap-0.5 bg-slate-100/80 p-0.5 rounded-md shrink-0">
                  <button
                    onClick={() => onCategoryChange("men")}
                    className={`px-3 sm:px-4 py-1 rounded font-semibold text-xs transition-all duration-200 ${
                      category === "men"
                        ? "bg-primary text-on-primary shadow-sm font-bold border-none"
                        : "bg-white text-slate-500 border border-slate-200 hover:text-primary hover:border-slate-300 hover:shadow-sm"
                    } focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 active:outline-none active:ring-0`}
                    type="button"
                  >
                    Men
                  </button>
                  <button
                    onClick={() => onCategoryChange("women")}
                    className={`px-3 sm:px-4 py-1 rounded font-semibold text-xs transition-all duration-200 ${
                      category === "women"
                        ? "bg-primary text-on-primary shadow-sm font-bold border-none"
                        : "bg-white text-slate-500 border border-slate-200 hover:text-primary hover:border-slate-300 hover:shadow-sm"
                    } focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 active:outline-none active:ring-0`}
                    type="button"
                  >
                    Women
                  </button>
                </div>
              </div>

              {/* Search & Filters */}
              <div className="product-search-actions flex items-center gap-1.5 sm:gap-2 lg:justify-end relative min-w-0 w-full lg:w-auto">
                <SearchBar
                  value={searchTerm}
                  onChange={onSearchChange}
                  placeholder="Search products..."
                  className="flex-1 min-w-0 w-full lg:w-48"
                />
                <FilterOptions
                  filters={apiFilters}
                  gender={category}
                  modelType={modelType}
                  onFilterChange={onFilterChange}
                  className="shrink-0"
                />
              </div>
            </div>
          </div>

          {/* Product sections container */}
          <div className="product-sections px-6 pt-1 pb-2">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 w-full text-center text-secondary">
                <MdSync className="text-[36px] text-slate-300 animate-spin mb-2" />
                <p className="text-xs font-semibold">
                  Loading garments catalog...
                </p>
              </div>
            ) : !hasAnyProducts ? (
              <div className="app-empty-state flex flex-col items-center justify-center py-20 w-full gap-2">
                <MdShoppingBag className="text-[48px] text-slate-300 mb-2" />
                <p className="text-sm font-semibold">
                  No products found matching your filters
                </p>
              </div>
            ) : (
              displayCollections.map((col, idx) => (
                <CollectionRail
                  key={col.id || col.title || `collection-${idx}`}
                  title={col.title}
                  products={col.products}
                  selectedProduct={selectedProduct}
                  userRole={userRole}
                  getGenerationCount={getGenerationCount}
                  handleProductCardClick={handleProductCardClick}
                  handleToggleProduct={handleToggleProduct}
                  handleEditModalOpen={handleEditModalOpen}
                  openZoomModal={openZoomModal}
                />
              ))
            )}
          </div>

          {pagination && pagination.totalPages > 1 && (
            <div className="product-pagination-container px-6 pb-16 shrink-0">
              <InternalPagination
                page={pagination.page}
                limit={pagination.limit}
                total={pagination.total}
                totalPages={pagination.totalPages}
                onPageChange={onPageChange}
              />
            </div>
          )}
        </div>

        {/* Sticky Bottom Action Bar */}
        <footer
          id="action-footer"
          className="app-footer-bar product-action-footer fixed bottom-0 left-56 right-0 px-6 h-12 flex items-center z-50 transition-all duration-300"
        >
          <div className="flex justify-between items-center w-full">
            <div
              id="selection-state-container"
              className="product-selection-state flex items-center gap-4 text-primary"
            >
              {selectedProduct ? (
                <>
                  <span className="font-body-md text-xs font-semibold text-secondary">
                    Selected:
                  </span>
                  <div className="flex items-center gap-2.5 animate-fade-in">
                    <div className="w-8 h-8 bg-surface-container rounded overflow-hidden border border-outline/50 shadow-sm flex-shrink-0">
                      <img
                        className="w-full h-full object-contain mix-blend-multiply"
                        src={selectedProduct.images?.[0]}
                        alt="selected"
                      />
                    </div>
                    <span className="text-xs font-bold text-primary truncate max-w-xs">
                      {selectedProduct.title}
                    </span>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2 text-secondary">
                  <MdInfo className="text-[18px]" />
                  <span className="font-body-md text-xs font-semibold text-secondary">
                    No product selected
                  </span>
                </div>
              )}
            </div>

            <button
              className={`px-6 py-2 rounded-md font-semibold text-xs transition-all shadow-sm focus:outline-none ${
                selectedProduct
                  ? "bg-primary text-on-primary hover:bg-primary/95 active:scale-[0.98] cursor-pointer"
                  : "bg-primary text-on-primary opacity-50 cursor-not-allowed"
              }`}
              onClick={handleContinue}
              disabled={!selectedProduct}
            >
              Continue
            </button>
          </div>
        </footer>
      </div>

      <ProductUploadModal
        isOpen={isUploadModalOpen}
        onClose={handleUploadModalClose}
        onSubmit={handleUploadSubmit}
        isLoading={isProductUploadLoading}
        getFilterCategories={fetchFilterCategories}
        getCollectionCategories={fetchCollectionCategories}
        defaultGender={category}
        defaultMode={mappedModelType}
        productToEdit={editingProduct}
      />

      <ZoomModal
        modalId="productZoomModal"
        front={zoomModal.front}
        back={zoomModal.back}
        onClose={closeZoomModal}
      />

      <MobileZoomModal
        isOpen={isMobileZoomOpen}
        onClose={closeZoomModal}
        front={zoomModal.front}
        back={zoomModal.back}
      />
    </>
  );
};

export default ProductSelector;

